import { useEffect, useMemo, useState } from "react";

import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import { Pagination } from "@mui/material";
import { useNavigate } from "react-router-dom";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import {
  ConsultationItem,
  ConsultationService,
  ConsultationStats,
} from "service/consultation/service";
import {
  AUCTION_INQUIRY_STATUS_COLOR,
  AUCTION_INQUIRY_STATUS_LABEL,
  AuctionInquiryStatus,
  INQUIRY_CATEGORY_LABEL,
  PAYMENT_COLOR,
  PAYMENT_LABEL,
  SERVICE_CATEGORY_LABEL,
} from "constants/consultation";

const SERVICE_TYPES = [
  { key: "ALL", label: "전체", color: "#1e9b6b" },
  { key: "AUCTION_ALLCARE", label: SERVICE_CATEGORY_LABEL.AUCTION_ALLCARE, color: "#3b82f6" },
  { key: "NPL_ALLCARE", label: SERVICE_CATEGORY_LABEL.NPL_ALLCARE, color: "#a855f7" },
  { key: "BID_AGENT", label: SERVICE_CATEGORY_LABEL.BID_AGENT, color: "#f97316" },
  { key: "RIGHTS_ANALYSIS", label: SERVICE_CATEGORY_LABEL.RIGHTS_ANALYSIS, color: "#14b8a6" },
  {
    key: "AUCTION_CONSULTATION",
    label: SERVICE_CATEGORY_LABEL.AUCTION_CONSULTATION,
    color: "#22c55e",
  },
] as const;

type ServiceKey = (typeof SERVICE_TYPES)[number]["key"];

const STATS_KEY_BY_SERVICE: Record<Exclude<ServiceKey, "ALL">, keyof ConsultationStats> = {
  AUCTION_ALLCARE: "auction_allcare_count",
  NPL_ALLCARE: "npl_allcare_count",
  BID_AGENT: "bid_agent_count",
  RIGHTS_ANALYSIS: "rights_analysis_count",
  AUCTION_CONSULTATION: "auction_consultation_count",
};

const STATUS_OPTIONS: { value: "ALL" | AuctionInquiryStatus; label: string }[] = [
  { value: "ALL", label: "전체" },
  ...Object.values(AuctionInquiryStatus).map((s) => ({
    value: s,
    label: AUCTION_INQUIRY_STATUS_LABEL[s],
  })),
];

const SORT_OPTIONS = [
  { value: "CD", label: "최신순" },
  { value: "CA", label: "오래된순" },
];

const formatKST = (utc: string | null | undefined): string => {
  if (!utc) return "";
  const iso = utc.includes("T")
    ? utc.endsWith("Z")
      ? utc
      : `${utc}Z`
    : `${utc.replace(" ", "T")}Z`;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return utc;
  const kst = new Date(d.getTime() + 9 * 60 * 60 * 1000);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${kst.getUTCFullYear()}-${pad(kst.getUTCMonth() + 1)}-${pad(kst.getUTCDate())} ${pad(
    kst.getUTCHours()
  )}:${pad(kst.getUTCMinutes())}:${pad(kst.getUTCSeconds())}`;
};

const EMPTY_STATS: ConsultationStats = {
  auction_allcare_count: "0",
  npl_allcare_count: "0",
  bid_agent_count: "0",
  rights_analysis_count: "0",
  auction_consultation_count: "0",
};

function ConsultationList(): JSX.Element {
  const [activeTab, setActiveTab] = useState<ServiceKey>("ALL");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [sort, setSort] = useState("CD");
  const [currentPage, setCurrentPage] = useState(1);

  const [list, setList] = useState<ConsultationItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState<ConsultationStats>(EMPTY_STATS);

  const navigate = useNavigate();

  const limit = 10;

  const fetchList = async () => {
    try {
      const res = await ConsultationService.list({
        search,
        serviceCategory: activeTab === "ALL" ? "" : activeTab,
        status: statusFilter === "ALL" ? "" : statusFilter,
        sort,
        limit,
        page: currentPage,
      });
      setList(res?.list ?? []);
      setTotalCount(res?.totalCount ?? 0);
      if (res?.stats) setStats(res.stats);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    fetchList();
  }, [currentPage]);

  useEffect(() => {
    setCurrentPage(1);
    fetchList();
  }, [activeTab, statusFilter, sort, search]);

  const totalPages = Math.max(1, Math.ceil(totalCount / limit));

  const totalForAll = useMemo(() => {
    return (
      Number(stats.auction_allcare_count || 0) +
      Number(stats.npl_allcare_count || 0) +
      Number(stats.bid_agent_count || 0) +
      Number(stats.rights_analysis_count || 0) +
      Number(stats.auction_consultation_count || 0)
    );
  }, [stats]);

  const getCount = (key: ServiceKey): number => {
    if (key === "ALL") return totalForAll;
    return Number(stats[STATS_KEY_BY_SERVICE[key]] || 0);
  };

  const handleSearch = () => {
    setSearch(searchInput);
  };

  const handleCsv = async () => {
    try {
      const query = new URLSearchParams();
      if (search) query.set("search", search);
      if (activeTab !== "ALL") query.set("serviceCategory", activeTab);
      if (statusFilter !== "ALL") query.set("status", statusFilter);
      if (sort) query.set("sort", sort);

      const token = localStorage.getItem("accessToken");
      const res = await fetch(
        `${process.env.REACT_APP_HOST}admin/auction/inquiry/csv?${query.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error(`CSV 다운로드 실패: ${res.status}`);

      const disposition = res.headers.get("content-disposition") ?? "";
      const match = disposition.match(/filename\*?=(?:UTF-8''|")?([^";]+)"?/i);
      const filename = match
        ? decodeURIComponent(match[1])
        : `consultation_${new Date().toISOString().slice(0, 10)}.csv`;

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox my={3}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
            gap: "12px",
            marginBottom: "16px",
          }}
        >
          {SERVICE_TYPES.filter((s) => s.key !== "ALL").map((s) => (
            <Card
              key={s.key}
              sx={{
                p: "16px 20px",
                borderLeft: `4px solid ${s.color}`,
                cursor: "pointer",
                transition: "transform 0.15s ease",
                "&:hover": { transform: "translateY(-2px)" },
              }}
              onClick={() => {
                setActiveTab(s.key);
              }}
            >
              <div
                style={{
                  fontSize: 13,
                  color: "#6b7280",
                  fontFamily: "Pretendard",
                  fontWeight: 500,
                  marginBottom: 6,
                }}
              >
                {s.label}
              </div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
                <span
                  style={{
                    fontSize: 26,
                    fontWeight: 700,
                    color: s.color,
                    fontFamily: "Pretendard",
                  }}
                >
                  {getCount(s.key)}
                </span>
                <span style={{ fontSize: 14, color: "#374151", fontFamily: "Pretendard" }}>건</span>
              </div>
            </Card>
          ))}
        </div>

        <div
          style={{
            display: "flex",
            gap: "8px",
            borderBottom: "1px solid #e5e7eb",
            marginBottom: "16px",
            overflowX: "auto",
          }}
        >
          {SERVICE_TYPES.map((s) => {
            const active = activeTab === s.key;
            return (
              <button
                key={s.key}
                type="button"
                onClick={() => setActiveTab(s.key)}
                style={{
                  background: "none",
                  border: "none",
                  padding: "12px 20px",
                  fontSize: 14,
                  fontWeight: active ? 700 : 500,
                  color: active ? "#111827" : "#6b7280",
                  borderBottom: active ? "2px solid #111827" : "2px solid transparent",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  fontFamily: "Pretendard",
                }}
              >
                {s.label}
              </button>
            );
          })}
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "8px",
            flexWrap: "wrap",
            marginBottom: "16px",
            padding: "0 4px",
          }}
        >
          <div style={{ display: "flex", flex: 1, minWidth: 320, gap: 6, alignItems: "center" }}>
            <input
              type="text"
              placeholder="고객명, 이메일, 문의내용 검색"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSearch();
              }}
              style={{
                flex: 1,
                padding: "10px 12px",
                border: "1px solid #d1d5db",
                borderRadius: 6,
                fontSize: 14,
                fontFamily: "Pretendard",
              }}
            />
            <div
              role="button"
              onClick={handleSearch}
              style={{
                padding: "10px 18px",
                backgroundColor: "rgb(30, 155, 107)",
                color: "white",
                fontSize: 14,
                fontWeight: 600,
                borderRadius: 6,
                cursor: "pointer",
                fontFamily: "Pretendard",
              }}
            >
              검색
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{
                padding: "10px 12px",
                border: "1px solid #d1d5db",
                borderRadius: 6,
                fontSize: 14,
                fontFamily: "Pretendard",
                backgroundColor: "white",
              }}
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              style={{
                padding: "10px 12px",
                border: "1px solid #d1d5db",
                borderRadius: 6,
                fontSize: 14,
                fontFamily: "Pretendard",
                backgroundColor: "white",
              }}
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <div
              role="button"
              onClick={handleCsv}
              style={{
                padding: "10px 14px",
                borderRadius: 6,
                border: "1px solid #111827",
                backgroundColor: "white",
                display: "flex",
                gap: 6,
                alignItems: "center",
                fontSize: 14,
                fontWeight: 500,
                cursor: "pointer",
                fontFamily: "Pretendard",
              }}
            >
              <Icon fontSize="small">file_download</Icon>
              CSV 다운로드
            </div>
          </div>
        </div>

        <Card>
          <div style={{ padding: "16px" }}>
            <div style={{ overflowX: "auto" }}>
              <table style={{ minWidth: 1000, width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: "#374151",
                      textAlign: "center",
                      fontFamily: "Pretendard",
                      borderBottom: "1px solid #e5e7eb",
                    }}
                  >
                    <th style={{ padding: "14px 8px" }}>No</th>
                    <th style={{ padding: "14px 8px" }}>서비스</th>
                    <th style={{ padding: "14px 8px" }}>고객명</th>
                    <th style={{ padding: "14px 8px" }}>이메일</th>
                    <th style={{ padding: "14px 8px" }}>상담분류</th>
                    <th style={{ padding: "14px 8px" }}>문의내용</th>
                    <th style={{ padding: "14px 8px" }}>신청일</th>
                    <th style={{ padding: "14px 8px" }}>결제상태</th>
                    <th style={{ padding: "14px 8px" }}>처리상태</th>
                    <th style={{ padding: "14px 8px" }}>상세보기</th>
                  </tr>
                </thead>
                <tbody>
                  {list.length === 0 && (
                    <tr>
                      <td
                        colSpan={10}
                        style={{
                          padding: "60px 0",
                          textAlign: "center",
                          color: "#9ca3af",
                          fontFamily: "Pretendard",
                          fontSize: 14,
                        }}
                      >
                        조회된 상담신청이 없습니다.
                      </td>
                    </tr>
                  )}
                  {list.map((row) => {
                    const service = SERVICE_TYPES.find((s) => s.key === row.service_category);
                    const paymentLabel = PAYMENT_LABEL[row.payment_status] ?? row.payment_status;
                    const paymentColor = PAYMENT_COLOR[row.payment_status] ?? "#6b7280";
                    const processLabel =
                      AUCTION_INQUIRY_STATUS_LABEL[row.current_status] ?? row.current_status;
                    const processColor =
                      AUCTION_INQUIRY_STATUS_COLOR[row.current_status] ?? "#6b7280";
                    const subCategory = row.inquiry_category
                      ? INQUIRY_CATEGORY_LABEL[row.inquiry_category] ?? row.inquiry_category
                      : "-";

                    return (
                      <tr
                        key={row.ai_id}
                        style={{
                          fontSize: 13,
                          color: "#000000DE",
                          textAlign: "center",
                          fontFamily: "Pretendard",
                          borderBottom: "1px solid #f3f4f6",
                        }}
                      >
                        <td style={{ padding: "16px 8px" }}>{row.ai_id}</td>
                        <td style={{ padding: "16px 8px" }}>
                          <span
                            style={{
                              display: "inline-block",
                              padding: "4px 10px",
                              borderRadius: 999,
                              backgroundColor: `${service?.color ?? "#6b7280"}1A`,
                              color: service?.color ?? "#6b7280",
                              fontSize: 12,
                              fontWeight: 600,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {service?.label ?? row.service_category}
                          </span>
                        </td>
                        <td style={{ padding: "16px 8px" }}>{row.name ?? row.user_name}</td>
                        <td style={{ padding: "16px 8px" }}>{row.email ?? row.user_email}</td>
                        <td
                          style={{
                            padding: "16px 8px",
                            maxWidth: 180,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          title={subCategory}
                        >
                          {subCategory}
                        </td>
                        <td
                          style={{
                            padding: "16px 8px",
                            maxWidth: 240,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            textAlign: "left",
                          }}
                          title={row.content}
                        >
                          {row.content}
                        </td>
                        <td style={{ padding: "16px 8px", whiteSpace: "nowrap" }}>
                          {formatKST(row.created_at)}
                        </td>
                        <td style={{ padding: "16px 8px" }}>
                          <span
                            style={{
                              display: "inline-block",
                              padding: "4px 10px",
                              borderRadius: 4,
                              backgroundColor: `${paymentColor}1A`,
                              color: paymentColor,
                              fontSize: 12,
                              fontWeight: 600,
                            }}
                          >
                            {paymentLabel}
                          </span>
                        </td>
                        <td style={{ padding: "16px 8px" }}>
                          <span
                            style={{
                              display: "inline-block",
                              padding: "4px 10px",
                              borderRadius: 4,
                              backgroundColor: `${processColor}1A`,
                              color: processColor,
                              fontSize: 12,
                              fontWeight: 600,
                            }}
                          >
                            {processLabel}
                          </span>
                        </td>
                        <td style={{ padding: "16px 8px" }}>
                          <MDButton
                            size="small"
                            variant="outlined"
                            color="dark"
                            onClick={() =>
                              navigate(`/management/consultation/${row.ai_id}`, {
                                state: { row },
                              })
                            }
                          >
                            상세보기
                          </MDButton>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ display: "flex", justifyContent: "center", marginTop: 24 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(_, value) => setCurrentPage(value)}
                color="secondary"
                sx={{
                  "& .MuiPaginationItem-root": {
                    "&.Mui-selected": {
                      backgroundColor: "#1e9b6b",
                      color: "white",
                    },
                    "&.Mui-selected:hover": {
                      backgroundColor: "#1e9b6b",
                    },
                  },
                }}
              />
            </div>
          </div>
        </Card>
      </MDBox>
    </DashboardLayout>
  );
}

export default ConsultationList;
